import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ExternalLink, Pencil, Plus, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { QuietEmpty } from '../shared';
import {
  useDeleteStationTool,
  useSaveStationTool,
  useStationTools,
} from '@/features/workMode/stationSetup';
import { describeWorkError } from '@/features/workMode/api';
import { useEquipmentList } from '@/features/equipment/hooks';
import type { FloorWorkstation, WorkstationTool } from '@/features/workMode/types';

interface StationToolsTabProps {
  station: FloorWorkstation;
  canManage: boolean;
}

const NO_EQUIPMENT = '__none__';

/**
 * Tools, fixtures and gauges that live at the bench. A tool can point at
 * the equipment register so the asset tag, manuals and maintenance follow
 * it; free-text is fine for hand tools nobody tracks as assets.
 */
export function StationToolsTab({ station, canManage }: StationToolsTabProps) {
  const { data: tools = [], isLoading } = useStationTools(station.id);
  const { data: equipment = [] } = useEquipmentList();
  const save = useSaveStationTool();
  const remove = useDeleteStationTool();
  const [editing, setEditing] = useState<WorkstationTool | 'new' | null>(null);

  const equipmentById = useMemo(() => new Map(equipment.map((e) => [e.id, e])), [equipment]);
  const required = tools.filter((t) => t.is_required).length;

  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= tools.length) return;
    const order = [...tools];
    [order[idx], order[j]] = [order[j], order[idx]];
    void Promise.all(
      order.map((t, i) => {
        const position = (i + 1) * 10;
        if (t.position === position) return Promise.resolve();
        return save.mutateAsync({
          id: t.id,
          stationId: station.id,
          name: t.name,
          equipmentId: t.equipment_id,
          quantity: t.quantity,
          notes: t.notes,
          isRequired: t.is_required,
          position,
        });
      }),
    ).catch((e) => toast.error(describeWorkError(e)));
  };

  const del = (t: WorkstationTool) => {
    if (!window.confirm(`Remove "${t.name}" from this station?`)) return;
    remove.mutate(
      { id: t.id, stationId: station.id },
      {
        onSuccess: () => toast.success('Tool removed.'),
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {tools.length === 0
            ? 'What has to be at the bench before anyone starts.'
            : `${tools.length} tool${tools.length === 1 ? '' : 's'} · ${required} required${tools.length - required ? ` · ${tools.length - required} nice to have` : ''}`}
        </p>
        {canManage && (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" />
            Add tool
          </Button>
        )}
      </div>

      {isLoading ? null : tools.length === 0 ? (
        <QuietEmpty>
          <span className="flex flex-col items-center gap-2">
            <Wrench className="h-6 w-6" />
            No tools listed yet.
          </span>
        </QuietEmpty>
      ) : (
        <ol className="divide-y divide-border rounded-2xl border border-border bg-card">
          {tools.map((t, i) => {
            const eq = t.equipment_id ? equipmentById.get(t.equipment_id) : undefined;
            return (
              <li key={t.id} className="flex items-start gap-3 px-4 py-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
                  <Wrench className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-foreground">
                      {t.quantity > 1 && (
                        <span className="tabular-nums text-muted-foreground">{t.quantity} × </span>
                      )}
                      {t.name}
                    </p>
                    {!t.is_required && <Badge variant="muted">Nice to have</Badge>}
                    {eq && (
                      <Link
                        to={`/catalog/equipment/${eq.id}`}
                        className="inline-flex items-center gap-1 text-xs text-info hover:underline"
                      >
                        {eq.asset_tag}
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                  {(t.notes || eq) && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {[eq ? [eq.manufacturer, eq.model].filter(Boolean).join(' ') : null, t.notes]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                </div>
                {canManage && (
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(i, -1)}
                      disabled={i === 0 || save.isPending}
                      aria-label="Move up"
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(i, 1)}
                      disabled={i === tools.length - 1 || save.isPending}
                      aria-label="Move down"
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(t)}
                      aria-label={`Edit ${t.name}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-fresh-salmon-dark"
                      onClick={() => del(t)}
                      disabled={remove.isPending}
                      aria-label={`Remove ${t.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {editing !== null && (
        <ToolDialog
          key={editing === 'new' ? 'new' : editing.id}
          station={station}
          tool={editing === 'new' ? null : editing}
          nextPosition={((tools.at(-1)?.position ?? 0) as number) + 10}
          equipment={equipment.map((e) => ({
            id: e.id,
            label: `${e.asset_tag} · ${e.name}`,
            name: e.name,
          }))}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

/** Mounted only while open so a background refetch never wipes a half-filled form. */
function ToolDialog({
  station,
  tool,
  nextPosition,
  equipment,
  onClose,
}: {
  station: FloorWorkstation;
  tool: WorkstationTool | null;
  nextPosition: number;
  equipment: Array<{ id: string; label: string; name: string }>;
  onClose: () => void;
}) {
  const save = useSaveStationTool();
  const [name, setName] = useState(tool?.name ?? '');
  const [equipmentId, setEquipmentId] = useState<string>(tool?.equipment_id ?? NO_EQUIPMENT);
  const [quantity, setQuantity] = useState(String(tool?.quantity ?? 1));
  const [notes, setNotes] = useState(tool?.notes ?? '');
  const [isRequired, setIsRequired] = useState(tool?.is_required ?? true);

  const pickEquipment = (id: string) => {
    setEquipmentId(id);
    const eq = equipment.find((e) => e.id === id);
    if (eq && !name.trim()) setName(eq.name);
  };

  const submit = () => {
    const qty = Number(quantity);
    if (!name.trim()) {
      toast.error('Give the tool a name.');
      return;
    }
    if (!Number.isInteger(qty) || qty < 1) {
      toast.error('Quantity has to be a whole number, at least 1.');
      return;
    }
    save.mutate(
      {
        id: tool?.id,
        stationId: station.id,
        name,
        equipmentId: equipmentId === NO_EQUIPMENT ? null : equipmentId,
        quantity: qty,
        notes,
        isRequired,
        ...(tool ? {} : { position: nextPosition }),
      },
      {
        onSuccess: () => {
          toast.success(tool ? 'Tool updated.' : 'Tool added.');
          onClose();
        },
        onError: (e) => toast.error(describeWorkError(e)),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{tool ? 'Edit tool' : 'Add a tool'}</DialogTitle>
          <DialogDescription>Kept at {station.name}.</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div className="space-y-1.5">
            <Label>From the equipment register (optional)</Label>
            <Select value={equipmentId} onValueChange={pickEquipment}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_EQUIPMENT}>Not a tracked asset</SelectItem>
                {equipment.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_96px]">
            <div className="space-y-1.5">
              <Label htmlFor="tool-name">Name</Label>
              <Input
                id="tool-name"
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                placeholder="1/4″ torque driver, 2.5 N·m"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tool-qty">Qty</Label>
              <Input
                id="tool-qty"
                inputMode="numeric"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tool-notes">Notes</Label>
            <Textarea
              id="tool-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Setting, bit size, where it hangs, calibration due…"
            />
          </div>
          <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
            <div>
              <p className="text-sm font-medium text-foreground">Required to run the station</p>
              <p className="text-xs text-muted-foreground">Off for nice-to-haves.</p>
            </div>
            <Switch checked={isRequired} onCheckedChange={setIsRequired} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || !name.trim()}>
              {save.isPending ? 'Saving…' : tool ? 'Save' : 'Add tool'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
